import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { UploadCloud, FileText, X, ArrowRight, ArrowLeft, Check, ShieldCheck, Lock, Database, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { API, track, getUtm } from "@/lib/okkio";
import Logo from "@/components/Logo";
import NoIndex from "@/components/NoIndex";

const LOADING_STEPS = [
  "Sto leggendo il tuo preventivo…",
  "Confronto prezzi e lavorazioni…",
  "Cerco eventuali criticità…",
  "Preparo il tuo controllo…",
];

const STEP_LABELS = ["Carica", "Analisi", "Risultato"];

function Stepper({ current }) {
  return (
    <div className="flex items-center gap-2 mb-6" data-testid="stepper">
      {STEP_LABELS.map((label, i) => {
        const active = i === current;
        const done = i < current;
        return (
          <div key={label} className="flex items-center gap-2 flex-1">
            <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0
              ${done ? "bg-[#16A36A] text-white" : active ? "bg-[#3157F6] text-white" : "bg-white border border-[#E2E8F0] text-[#64748B]"}`}>
              {i + 1}
            </span>
            <span className={`text-sm font-medium ${active || done ? "text-[#0F172A]" : "text-[#64748B]"} hidden xs:inline`}>{label}</span>
            {i < STEP_LABELS.length - 1 && <span className={`h-0.5 flex-1 rounded ${done ? "bg-[#16A36A]" : "bg-[#E2E8F0]"}`} />}
          </div>
        );
      })}
    </div>
  );
}

export default function Analyze() {
  const navigate = useNavigate();
  const [step, setStep] = useState("upload"); // upload | loading
  const [file, setFile] = useState(null);
  const [drag, setDrag] = useState(false);
  const [loadingIdx, setLoadingIdx] = useState(0);
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const onFile = (f) => {
    if (!f) return;
    const ok = ["application/pdf", "image/jpeg", "image/png"].includes(f.type);
    if (!ok) { toast.error("Formato non supportato. Usa PDF, JPG o PNG."); return; }
    if (f.size > 15 * 1024 * 1024) { toast.error("File troppo grande (max 15MB)."); return; }
    setFile(f);
    track("file_selected", { ext: (f.name.split(".").pop() || "").toLowerCase() });
  };

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    onFile(e.dataTransfer.files?.[0]);
  };

  const openPicker = () => inputRef.current?.click();

  const cycleLoading = () => {
    let i = 0;
    setLoadingIdx(0);
    // 4 micro-fasi distribuite sui ~50s; si ferma sull'ultima se l'analisi dura di più.
    const id = setInterval(() => {
      i = Math.min(i + 1, LOADING_STEPS.length - 1);
      setLoadingIdx(i);
    }, 12500);
    timerRef.current = id;
    return id;
  };

  const submit = async () => {
    if (!file) { openPicker(); return; }
    track("analyze_click");
    setStep("loading");
    const timer = cycleLoading();
    try {
      const fd = new FormData();
      fd.append("file", file);
      const utm = getUtm();
      Object.entries(utm).forEach(([k, v]) => { if (v) fd.append(k, v); });
      const { data } = await axios.post(`${API}/analyze`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const token = data.token;
      // File effettivamente inviato e accettato dal backend (status processing).
      track("upload_complete", { ext: (file.name.split(".").pop() || "").toLowerCase() });
      let attempts = 0;
      const poll = async () => {
        try {
          const { data: st } = await axios.get(`${API}/analyze/status/${token}`);
          if (st.status === "done" || st.status === "not_eligible") {
            clearInterval(timer);
            navigate(`/report/${token}`);
            return;
          }
          if (st.status === "error" || attempts > 60) {
            clearInterval(timer);
            toast.error("Analisi non riuscita. Riprova.");
            setStep("upload");
            return;
          }
          attempts += 1;
          setTimeout(poll, 3000);
        } catch {
          if (attempts > 60) { clearInterval(timer); toast.error("Analisi non riuscita. Riprova."); setStep("upload"); return; }
          attempts += 1;
          setTimeout(poll, 3000);
        }
      };
      poll();
    } catch (e) {
      clearInterval(timer);
      toast.error(e?.response?.data?.detail || "Analisi non riuscita. Riprova.");
      setStep("upload");
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F9FC]">
      <NoIndex />
      <header className="max-w-2xl mx-auto px-5 py-4 flex items-center justify-between">
        <button onClick={() => navigate("/")} data-testid="nav-logo" className="flex items-center"><Logo className="text-2xl" /></button>
        <button onClick={() => navigate("/")} data-testid="back-home"
          className="inline-flex items-center gap-1.5 rounded-full border border-[#E2E8F0] bg-white px-4 h-10 text-sm font-semibold text-[#334155] hover:border-[#3157F6] hover:text-[#3157F6] transition-colors">
          <ArrowLeft className="w-4 h-4" strokeWidth={2} /> Torna alla Home
        </button>
      </header>

      <main className="max-w-2xl mx-auto px-5 pb-16">
        <Stepper current={step === "upload" ? 0 : 1} />
        <AnimatePresence mode="wait">
          {step === "upload" && (
            <motion.div key="upload" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)} onDrop={onDrop}>
              <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">Carica il preventivo</h1>
              <p className="mt-2 text-sm sm:text-base text-slate-600">
                Scegli il PDF o una foto del preventivo. <span className="font-semibold text-[#0F172A]">Nessuna registrazione</span> — l'analisi parte subito.
              </p>

              <input ref={inputRef} id="file-input" data-testid="file-input" type="file"
                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />

              {/* PULSANTE PRINCIPALE: 'Seleziona file' -> (dopo scelta) 'Analizza il mio preventivo' */}
              <Button data-testid="primary-action" onClick={submit}
                className={`mt-5 w-full rounded-2xl h-16 px-6 text-lg font-semibold text-white active:scale-[0.98] transition-transform shadow-[0_14px_34px_-12px_rgba(49,87,246,0.75)] ${drag ? "bg-[#2444D8]" : "bg-[#3157F6] hover:bg-[#2444D8]"}`}>
                {file
                  ? (<><ShieldCheck className="w-6 h-6 mr-2" strokeWidth={2} /> Analizza il mio preventivo</>)
                  : (<><UploadCloud className="w-6 h-6 mr-2" strokeWidth={2} /> Seleziona file</>)}
              </Button>
              <p className="mt-2.5 text-center text-sm font-medium text-[#64748B]" data-testid="upload-microcopy">
                PDF o foto • max 15MB • Prima analisi gratuita
              </p>

              {file && (
                <div data-testid="file-chip" className="mt-4 flex items-center gap-3 rounded-xl border border-[#C7D2FE] bg-[#F5F7FF] p-4">
                  <FileText className="w-5 h-5 text-[#3157F6]" strokeWidth={1.75} />
                  <span className="flex-1 truncate text-sm font-medium text-slate-700">{file.name}</span>
                  <button onClick={() => { setFile(null); if (inputRef.current) inputRef.current.value = ""; }} data-testid="remove-file" aria-label="Rimuovi file">
                    <X className="w-4 h-4 text-slate-400" />
                  </button>
                </div>
              )}

              {!file && (
                <button onClick={openPicker} data-testid="pick-hint"
                  className="mt-4 w-full text-center text-sm text-slate-400 hover:text-[#3157F6] transition-colors">
                  oppure trascina qui il file
                </button>
              )}

              <div className="mt-6 rounded-2xl border border-[#E2E8F0] bg-white p-4" data-testid="trust-block">
                <div className="grid grid-cols-2 gap-x-3 gap-y-3">
                  {[
                    { icon: Lock, t: "Il tuo preventivo resta privato" },
                    { icon: ShieldCheck, t: "Nessuna registrazione" },
                    { icon: Database, t: "Confronto con benchmark reali e fonti" },
                    { icon: EyeOff, t: "Puoi oscurare nomi, indirizzi e dati personali" },
                  ].map((x) => (
                    <div key={x.t} className="flex items-start gap-2">
                      <x.icon className="w-4 h-4 text-[#3157F6] mt-0.5 flex-shrink-0" strokeWidth={2} />
                      <span className="text-xs font-medium text-[#334155] leading-snug">{x.t}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-3 pt-3 border-t border-[#F1F5F9] text-xs text-[#64748B] leading-snug" data-testid="trust-benchmark-line">
                  OKKIO confronta il preventivo con un <span className="font-semibold text-[#0F172A]">database strutturato di prezzi, lavorazioni e benchmark</span>, non con una risposta generica.
                </p>
              </div>

              <p className="mt-4 text-xs text-[#64748B] leading-relaxed text-center" data-testid="analyze-privacy-note">
                Caricando il preventivo dichiari di aver preso visione dell'<a href="/privacy-policy" className="font-semibold text-[#3157F6] hover:underline">Informativa Privacy</a>. Non inserire dati personali non necessari all'analisi.
              </p>
            </motion.div>
          )}

          {step === "loading" && (
            <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="pt-14 flex flex-col items-center text-center" data-testid="loading-screen">
              <div className="relative w-16 h-16 mb-8">
                <div className="absolute inset-0 rounded-full border-4 border-[#E2E8F0]" />
                <motion.div className="absolute inset-0 rounded-full border-4 border-[#3157F6] border-t-transparent"
                  animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1.3, ease: "linear" }} />
              </div>
              <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-[#0F172A]">Dacci solo 50 secondi. Ne vale la pena.</h2>
              <p className="mt-2 text-[#64748B] max-w-sm">Stiamo controllando davvero il tuo preventivo, voce per voce. Prezzi, lavorazioni e possibili criticità.</p>
              <div className="mt-8 w-full max-w-sm space-y-3.5 text-left">
                {LOADING_STEPS.map((s, i) => {
                  const done = i < loadingIdx, active = i === loadingIdx;
                  return (
                    <div key={s} className={`flex items-center gap-3 text-base transition-colors duration-500 ${done || active ? "text-[#0F172A]" : "text-[#94A3B8]"}`}>
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0
                        ${done ? "bg-[#16A36A]" : active ? "bg-[#3157F6] okkio-dot" : "bg-[#E2E8F0]"}`}>
                        {done && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
                      </span>
                      {s}
                    </div>
                  );
                })}
              </div>
              <p className="mt-8 text-sm font-medium text-[#3157F6]" data-testid="loading-reassure">Resta qui, ci siamo quasi.</p>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
